import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { lazy, Suspense, useEffect, useState } from 'react';
import { isApiError, isTenantReason } from '@agnivo/api-client';
import { useApi } from '../api/ApiProvider';
import { isIdentityNotProvisioned } from '../api/identityGap';
import { RouteFallback } from '../components/RouteFallback';
import { readDocumentLocale } from '../lib/documentLocale';
// The confirm screen (and the checkbox it shares with AcceptanceStep) is a lazy chunk: every session runs
// the probe, only a fresh registrant renders the screen, so the entry chunk must not carry it.
const RegistrationConfirm = lazy(() => import('./RegistrationConfirm').then((m) => ({ default: m.RegistrationConfirm })));
/**
 * ADR-182 D1.3 (W3): the proactive registration bootstrap. A verified Zitadel session with no platform
 * account (a web registrant who just verified their e-mail) makes ONE probe call; on
 * IDENTITY_NOT_PROVISIONED it shows the confirm step (name + verified address beside the single fixed
 * checkbox — this confirm IS the acceptance, D2.7), calls `accounts.complete` once, then mounts the app.
 *
 * Mounted INSIDE `AuthGate` (a token is required for preview/complete) and OUTSIDE `IdentityGapGate`, so
 * the gap latch only ever sees a `complete` that itself refused: any refusal here falls through to
 * `done` and the app's own calls latch the gap exactly as before. `ApiProvider.onApiError` stays
 * side-effect-only and unchanged (a probe error still reaches it and latches the gap first — harmless:
 * the gate below is mounted only after this resolves, and the latch is read by IdentityGapGate then).
 *
 * ⚠️ The identity-gap latch is STICKY and sees every API error, including this probe's. The caller
 * therefore suppresses IDENTITY_NOT_PROVISIONED while the bootstrap is in flight and stops suppressing in
 * `onResolved` (called on EVERY exit, before the app mounts): after a successful `complete` nothing
 * latched; after a refused preview/complete the app's own calls latch the gap as they always did.
 *
 * Single-flight: the probe/complete run once per mount (effect-guarded); N children never start N of them.
 */
export function RegistrationBootstrap({ client, lang, onResolved, children }) {
    const [phase, setPhase] = useState({ kind: 'probing' });
    const [checked, setChecked] = useState(false);
    const [stale, setStale] = useState(false);
    const [busy, setBusy] = useState(false);
    const finish = () => {
        onResolved?.();
        setPhase({ kind: 'done' });
    };
    useEffect(() => {
        const ac = new AbortController();
        (async () => {
            try {
                await client.agents.list({ pageSize: 1 }, ac.signal);
                finish();
            }
            catch (e) {
                if (ac.signal.aborted)
                    return;
                if (!isIdentityNotProvisioned(e))
                    return finish();
                try {
                    setPhase({ kind: 'confirm', preview: await client.accounts.preview(ac.signal) });
                }
                catch {
                    // No verified e-mail / route dark / anything else: the identity-gap card is the honest outcome.
                    if (!ac.signal.aborted)
                        finish();
                }
            }
        })();
        return () => ac.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- `finish` only calls the stable onResolved + setPhase
    }, [client]);
    if (phase.kind === 'probing')
        return _jsx(RouteFallback, {});
    if (phase.kind === 'done')
        return _jsx(_Fragment, { children: children });
    const confirm = async (termsVersion, privacyVersion) => {
        setBusy(true);
        try {
            await client.accounts.complete({
                termsDocumentVersion: termsVersion,
                privacyPolicyDocumentVersion: privacyVersion,
            });
            finish();
        }
        catch (e) {
            if (isApiError(e) && isTenantReason(e, 'DOCUMENT_VERSION_NOT_STANDING')) {
                // The text moved under the user: re-read, clear the tick, ask again (product §5).
                setChecked(false);
                setStale(true);
                try {
                    setPhase({ kind: 'confirm', preview: await client.accounts.preview() });
                }
                catch {
                    finish();
                }
            }
            else {
                finish();
            }
        }
        finally {
            setBusy(false);
        }
    };
    return (_jsx(Suspense, { fallback: _jsx(RouteFallback, {}), children: _jsx(RegistrationConfirm, { preview: phase.preview, lang: lang, checked: checked, onChecked: setChecked, stale: stale, busy: busy, onConfirm: (t, p) => void confirm(t, p) }) }));
}
/** Reads the client from `ApiProvider` so `App.tsx` can mount the bootstrap as a plain wrapper. */
export function RegistrationGate({ onResolved, children }) {
    const { client } = useApi();
    return (_jsx(RegistrationBootstrap, { client: client, lang: readDocumentLocale() === 'en' ? 'en' : 'ru', onResolved: onResolved, children: children }));
}
/**
 * Suppresses IDENTITY_NOT_PROVISIONED for the identity-gap latch while the bootstrap is in flight (see
 * `RegistrationBootstrap`). A plain object rather than a ref so the handler identity is stable and the
 * mutation is invisible to render (react-hooks flags ref reads that reach a render-time call).
 */
export function createIdentitySuppression() {
    let active = true;
    return {
        wrap: (next) => (err) => {
            if (active && isIdentityNotProvisioned(err))
                return;
            next(err);
        },
        resolve: () => {
            active = false;
        },
    };
}
//# sourceMappingURL=RegistrationBootstrap.js.map